// Feature: lead-binder-business-tabs
//
// Centralized endpoint contract for the Lead Binder data service.
//
// This module is the single place where the Business Policy binder endpoints are
// mapped. The SAME `/policy/...` endpoints are used for both scopes — the only
// difference is the scope key sent to them: `customer_policy_id` in the
// customer-policy context, and `lead_id` in the lead-binder context. Keeping the
// mapping in one file means confirming or changing the backend contract is a
// one-file change with no component or thunk edits.

/**
 * The Business Insurance Binder sub-tabs whose fetch/save are re-keyed by lead_id.
 *
 * The first group is the original set; the second group is the additional sub-tabs
 * (Schedules items, Questionnaire items, SOV, and Business Interruption) added by the
 * lead-binder-additional-tabs feature.
 */
export type BinderSubTab =
  | "underwriting"
  | "risk-location"
  | "interested-party"
  | "additional-interest"
  | "coverage"
  | "coverage-exclusions"
  | "claims"
  | "marketing"
  // Additional sub-tabs (lead-binder-additional-tabs)
  | "equipment"
  | "tool-floater"
  | "fine-art"
  | "contractor"
  | "restaurant"
  | "sov"
  | "business-interruption";

/**
 * Scope descriptor threaded through the Lead Binder data service. Both scopes hit the
 * SAME `/policy/...` endpoint; only the scope key differs (`customer_policy_id` vs
 * `lead_id`). The two variants are mutually exclusive by construction.
 */
export type BinderScopeArg =
  | { kind: "policy"; customerPolicyId: number; customerId?: number }
  | { kind: "lead"; leadId: string };

/**
 * Client-side scope descriptor for the lead-binder context. `customerPolicyId` is typed
 * as `never` so it is impossible to attach a customer policy id to a lead-scoped request
 * at the type level (Requirements 3.4, 4.5).
 */
export interface IBinderRequestScope {
  leadId: string;
  customerPolicyId?: never;
}

/**
 * Lead-scope descriptor for the additional Schedules/Questionnaire/SOV/Business
 * Interruption sub-tab thunks (lead-binder-additional-tabs). Identical in shape to
 * {@link IBinderRequestScope}: `customerPolicyId` is typed as `never` so attaching a
 * customer policy id to a lead-scoped request is a compile-time error
 * (Requirements 2.9, 4.4).
 */
export interface ILeadScheduleScope {
  leadId: string;
  customerPolicyId?: never;
}

/**
 * Per-sub-tab endpoint pair (fetch = GET, save = POST). The SAME endpoints serve both
 * the customer-policy and lead-binder scopes; only the scope key sent to them differs.
 */
interface IBinderEndpointPair {
  /** GET endpoint path (query params are appended by the request builder). */
  fetch: string;
  /** POST endpoint path (scope key travels in the request body). */
  save: string;
}

/**
 * The endpoint map. These are the existing Business Policy `/policy/...` routes and are
 * used unchanged for both scopes — the customer-policy context sends
 * `customer_policy_id` and the lead-binder context sends `lead_id` to the same URL.
 */
export const LEAD_BINDER_ENDPOINTS: Record<BinderSubTab, IBinderEndpointPair> = {
  underwriting: {
    fetch: "/policy/get-policy-business-underwriting",
    save: "/policy/edit-policy-business-underwriting",
  },
  "risk-location": {
    fetch: "/policy/get-policy-business-risk-location",
    save: "/policy/edit-policy-business-risk-location",
  },
  "interested-party": {
    fetch: "/policy/get-policy-business-interested-parties",
    save: "/policy/edit-policy-business-interested-parties",
  },
  "additional-interest": {
    fetch: "/policy/get-policy-business-additional-interest",
    save: "/policy/edit-policy-business-additional-interest",
  },
  coverage: {
    fetch: "/policy/get-policy-business-coverage-summary",
    save: "/policy/edit-policy-business-coverage-summary",
  },
  "coverage-exclusions": {
    fetch: "/policy/get-policy-business-coverage-exclusions",
    // NOTE: the backend save route is singular ("exclusion"), while the fetch route is
    // plural ("exclusions"). Confirmed against the live API.
    save: "/policy/edit-policy-business-coverage-exclusion",
  },
  claims: {
    fetch: "/policy/get-policy-business-claims",
    save: "/policy/create-policy-business-claims",
  },
  marketing: {
    fetch: "/policy/get-marketing-history",
    save: "/policy/edit-policy-add-marketing-history",
  },
  // Additional sub-tabs (lead-binder-additional-tabs). Same `/policy/...` (and
  // `/import-utility/...` for SOV) routes as the customer-policy context; only the
  // scope key sent to them differs (`lead_id` vs `customer_policy_id`).
  equipment: {
    fetch: "/policy/get-policy-business-equipment",
    save: "/policy/edit-policy-business-equipment",
  },
  "tool-floater": {
    fetch: "/policy/get-policy-business-tool-floater",
    save: "/policy/edit-policy-business-tool-floater",
  },
  "fine-art": {
    fetch: "/policy/get-policy-business-fine-art",
    save: "/policy/edit-policy-business-fine-art",
  },
  contractor: {
    fetch: "/policy/get-policy-business-contractor",
    save: "/policy/edit-policy-business-contractor",
  },
  restaurant: {
    fetch: "/policy/get-policy-business-restaurant",
    save: "/policy/edit-policy-business-restaurant",
  },
  sov: {
    fetch: "/import-utility/get-sov-import",
    save: "/import-utility/import-sov",
  },
  "business-interruption": {
    fetch: "/policy/get-policy-business-interruption",
    save: "/policy/create-policy-business-interruption",
  },
};

/** The kind of request the builder should produce. */
export type BinderRequestOperation = "fetch" | "save";

/**
 * The result of building a binder request: the resolved URL (including any query
 * scope key for GETs) and the scope-key fields to merge into the request body for POSTs.
 */
export interface IBuiltBinderRequest {
  /** HTTP method implied by the operation. */
  method: "GET" | "POST";
  /** Fully-resolved URL. For fetches this includes the scope query parameter. */
  url: string;
  /**
   * Scope-key fields to merge into the POST body. In lead scope this is
   * `{ lead_id }` and never contains `customer_policy_id`; in policy scope it is
   * `{ customer_policy_id, customer_id? }` and never contains `lead_id`.
   */
  body: Record<string, string | number>;
}

/**
 * Pure URL/payload builder for the Lead Binder data service.
 *
 * Both scopes hit the same `/policy/...` endpoint. In lead scope it carries `lead_id`
 * and omits `customer_policy_id` from both the query string and the body
 * (Requirements 3.1, 3.2, 3.4, 4.5). In policy scope it is keyed by
 * `customer_policy_id`, leaving the existing customer-policy contract unchanged
 * (Requirement 4.2).
 *
 * This function performs no I/O; it only computes the request shape so it can be
 * exhaustively property-tested.
 */
export const buildBinderRequest = (
  subTab: BinderSubTab,
  scope: BinderScopeArg,
  operation: BinderRequestOperation = "fetch"
): IBuiltBinderRequest => {
  // Same endpoint for both scopes; only the scope key differs.
  const path = LEAD_BINDER_ENDPOINTS[subTab][operation];

  if (scope.kind === "lead") {
    if (operation === "fetch") {
      return {
        method: "GET",
        url: `${path}?lead_id=${encodeURIComponent(scope.leadId)}`,
        body: {},
      };
    }
    return {
      method: "POST",
      url: path,
      body: { lead_id: scope.leadId },
    };
  }

  // Policy scope: keyed by customer_policy_id.
  if (operation === "fetch") {
    return {
      method: "GET",
      url: `${path}?customer_policy_id=${scope.customerPolicyId}`,
      body: {},
    };
  }

  const body: Record<string, string | number> = {
    customer_policy_id: scope.customerPolicyId,
  };
  if (scope.customerId !== undefined) {
    body.customer_id = scope.customerId;
  }
  return {
    method: "POST",
    url: path,
    body,
  };
};
